import * as R from 'Router/routes';
import {
  AsyncAdditionalAmount,
  AsyncExtensions,
  AsyncFullRepayment,
  AsyncRepayment,
} from 'Router/asyncRoutes';
import { DEFAULT_DATE_FORMAT } from 'utils/translate';
import { isProductKind } from 'utils/getProductNumber';
import { PRODUCT_NUMBER_IL } from 'utils/constants';
import { useCallback, useEffect, useState } from 'react';
import { useLatestLoan, usePaymentSchedule } from 'hooks/webapi';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

export const useActiveLoan = () => {
  const { latestLoan } = useLatestLoan();
  const { paymentSchedule } = usePaymentSchedule();
  const navigate = useNavigate();

  /**
   * State
   */
  const [isLegalTermsOpen, setIsLegalTermsOpen] = useState(false);

  /**
   * Lifecycle
   */
  useEffect(() => {
    AsyncRepayment.preload();
    AsyncAdditionalAmount.preload();
    AsyncFullRepayment.preload();
    AsyncExtensions.preload();
  });

  /**
   * Functions
   */
  const navigateToRepayment = useCallback(() => {
    navigate(R.REPAYMENT);
  }, [navigate]);

  const getNextPaymentDueDate = useCallback(() => {
    let nextPayment = paymentSchedule?.items.find(
      ({ status }) => status !== 'PAID',
    );

    // If all items are paid but penalties are pending
    if (!nextPayment && paymentSchedule?.items.length) {
      const lastElemPos = paymentSchedule?.items.length - 1;
      nextPayment = paymentSchedule?.items[lastElemPos];
    }

    return (
      nextPayment?.dueDate &&
      dayjs(nextPayment?.dueDate).format(DEFAULT_DATE_FORMAT)
    );
  }, [paymentSchedule]);

  /**
   * Computed Variables
   */
  const { overDueDays, productCode } = latestLoan || {};
  const is7CONS = isProductKind(latestLoan, PRODUCT_NUMBER_IL);
  const isOverdue = Boolean(latestLoan && latestLoan.overDueDays > 0);
  const isBrokenAgreement =
    Boolean(latestLoan?.debtCollectLoanAgreement) ||
    Boolean(latestLoan && latestLoan.overDueDays > 30);

  const shouldHideInvoiceLink = Boolean(overDueDays && is7CONS); // CWT-1633

  return {
    isLegalTermsOpen,
    setIsLegalTermsOpen,
    navigateToRepayment,
    getNextPaymentDueDate,
    is7CONS,
    isOverdue,
    isBrokenAgreement,
    productCode,
    shouldHideInvoiceLink,
  };
};
